import { useState, useCallback, useMemo, useRef } from "react";
import type { TagType } from "@/types/entities/tags";
import type { UpdateDeadlineSuspensionConfigType } from "@/queries/deadline-suspensions/deadline-suspensions.api";

interface BaseConfig {
  deadlineId?: string;
  id: string;
  maxDays: number;
  toSave: boolean;
}

export interface TagConfig extends BaseConfig {
  type: "tag";
  useGeneralConfig: boolean;
}

export interface ProcessConfig extends BaseConfig {
  type: "process";
  tagId: string;
}

export type SuspensionConfig = TagConfig | ProcessConfig;

export interface SuspensionState {
  configs: SuspensionConfig[];
}

/**
 * Creates initial state from API data
 */
function createInitialState(tags: TagType[]): SuspensionState {
  const configs: SuspensionConfig[] = [];

  tags.forEach((tag) => {
    const globalConfig = tag.global_deadline_suspension_config;
    const tagMaxDays = globalConfig?.max_days ?? 0;

    let isGlobal: boolean;

    if (globalConfig?.is_global != null) {
      isGlobal = globalConfig.is_global;
    } else {
      const hasProcessWithOwnConfig = tag.processes?.some(
        (process) => process.deadline_suspension_config != null,
      );
      isGlobal = !hasProcessWithOwnConfig; // false if any process has config
    }

    // Add tag config
    configs.push({
      deadlineId: globalConfig?.id,
      type: "tag",
      id: tag.id,
      maxDays: tagMaxDays,
      useGeneralConfig: isGlobal,
      toSave: false,
    });

    // Add process configs
    tag.processes?.forEach((process) => {
      let processMaxDays: number;

      if (isGlobal) {
        // Tag is global → use tag's max_days
        processMaxDays = tagMaxDays;
      } else {
        // Tag is NOT global → check if process has its own config
        const processConfig = process.deadline_suspension_config;
        processMaxDays = processConfig?.max_days ?? tagMaxDays;
      }

      configs.push({
        deadlineId: process.deadline_suspension_config?.id,
        type: "process",
        id: process.id,
        tagId: tag.id,
        maxDays: processMaxDays,
        toSave: false,
      });
    });
  });

  return { configs };
}

/**
 * Check if there are any changes to save
 * Checks both current page configs and pending changes from other pages
 */
function hasAnyChanges(
  state: SuspensionState,
  pendingChanges: Map<string, SuspensionConfig>,
): boolean {
  // Check if any config in current page has toSave flag
  const hasCurrentPageChanges = state.configs.some((config) => config.toSave);

  // Check if there are any pending changes from other pages
  const hasPendingChanges = pendingChanges.size > 0;

  return hasCurrentPageChanges || hasPendingChanges;
}

/**
 * Get only the changes marked with toSave flag
 * Includes changes from current page and pending changes from other pages
 */
function getChangesToSave(
  state: SuspensionState,
  pendingChanges: Map<string, SuspensionConfig>,
): UpdateDeadlineSuspensionConfigType {
  const configs: UpdateDeadlineSuspensionConfigType["configs"] = [];

  // Collect all tag configs to process (current page + pending from other pages)
  const tagsToSaveMap = new Map<string, TagConfig>();
  const tagsWithProcessChanges = new Set<string>();

  // Add tags from current page
  state.configs
    .filter(
      (config): config is TagConfig => config.type === "tag" && config.toSave,
    )
    .forEach((tag) => tagsToSaveMap.set(tag.id, tag));

  // Add pending tag changes from other pages
  Array.from(pendingChanges.values())
    .filter(
      (config): config is TagConfig => config.type === "tag" && config.toSave,
    )
    .forEach((tag) => tagsToSaveMap.set(tag.id, tag));

  // Collect tags that have processes with changes
  state.configs
    .filter(
      (config): config is ProcessConfig =>
        config.type === "process" && config.toSave,
    )
    .forEach((process) => tagsWithProcessChanges.add(process.tagId));

  Array.from(pendingChanges.values())
    .filter(
      (config): config is ProcessConfig =>
        config.type === "process" && config.toSave,
    )
    .forEach((process) => tagsWithProcessChanges.add(process.tagId));

  // Add tags that have process changes but the tag itself doesn't have changes
  tagsWithProcessChanges.forEach((tagId) => {
    if (!tagsToSaveMap.has(tagId)) {
      const tag = state.configs.find(
        (c): c is TagConfig => c.type === "tag" && c.id === tagId,
      );
      if (tag) {
        tagsToSaveMap.set(tagId, tag);
      }
    }
  });

  // Process all tags with changes
  tagsToSaveMap.forEach((tagConfig) => {
    if (tagConfig.useGeneralConfig) {
      // is_global = true → Send tag config with tag_id
      configs.push({
        is_global: true,
        tag_id: tagConfig.id,
        max_days: tagConfig.maxDays,
        id: tagConfig.deadlineId,
      });
    } else {
      // is_global = false → Only send tag if it has its own changes
      if (tagConfig.toSave) {
        configs.push({
          is_global: false,
          tag_id: tagConfig.id,
          max_days: tagConfig.maxDays,
          id: tagConfig.deadlineId,
        });
      }

      // Send each process_id with changes separately
      // Get processes from current page
      const currentPageProcesses = state.configs.filter(
        (config): config is ProcessConfig =>
          config.type === "process" &&
          config.tagId === tagConfig.id &&
          config.toSave,
      );

      // Get pending processes from other pages
      const pendingProcesses = Array.from(pendingChanges.values()).filter(
        (config): config is ProcessConfig =>
          config.type === "process" &&
          config.tagId === tagConfig.id &&
          config.toSave,
      );

      // Combine and deduplicate by id
      const processMap = new Map<string, ProcessConfig>();
      [...currentPageProcesses, ...pendingProcesses].forEach((proc) =>
        processMap.set(proc.id, proc),
      );

      processMap.forEach((processConfig) => {
        configs.push({
          is_global: false,
          process_id: processConfig.id,
          max_days: processConfig.maxDays,
          id: processConfig.deadlineId,
        });
      });
    }
  });

  return { configs };
}

export function useSuspensionState() {
  const [state, setState] = useState<SuspensionState>({ configs: [] });

  // Keep reference to initial state for change detection
  const initialStateRef = useRef<SuspensionState>({ configs: [] });

  // Keep reference to latest state for stable callbacks
  const stateRef = useRef<SuspensionState>({ configs: [] });
  stateRef.current = state;

  // Keep ALL changes across pages (key: id, value: config with toSave=true)
  const pendingChangesRef = useRef<Map<string, SuspensionConfig>>(new Map());

  /**
   * Initialize/Update state with new page data
   * Preserves pending changes from previous pages
   */
  const initialize = useCallback((data: TagType[]) => {
    const newState = createInitialState(data);

    // Save fresh data from service as initial state
    initialStateRef.current = { configs: [...newState.configs] };

    // Merge pending changes back into new state for display
    const mergedConfigs = newState.configs.map((config) => {
      const pendingChange = pendingChangesRef.current.get(config.id);
      if (pendingChange) {
        // Use pending change instead of fresh data
        return pendingChange;
      }
      return config;
    });

    const mergedState = { configs: mergedConfigs };
    setState(mergedState);
  }, []);

  /**
   * Update tag's max days
   */
  const updateTagMaxDays = useCallback((tagId: string, maxDays: number) => {
    setState((prev) => {
      // Just map and replace the modified item
      const newConfigs = prev.configs.map((c) => {
        if (c.type === "tag" && c.id === tagId) {
          const updated = { ...c, maxDays, toSave: true };
          pendingChangesRef.current.set(tagId, updated);
          return updated;
        }
        return c;
      });
      return { configs: newConfigs };
    });
  }, []);

  /**
   * Toggle general configuration for a tag
   */
  const toggleGeneralConfig = useCallback((tagId: string, enabled: boolean) => {
    setState((prev) => {
      const newConfigs = prev.configs.map((c) => {
        // Update the tag
        if (c.type === "tag" && c.id === tagId) {
          const updated = { ...c, useGeneralConfig: enabled, toSave: true };
          pendingChangesRef.current.set(tagId, updated);
          return updated;
        }

        // If switching to individual, mark all processes to save
        if (!enabled && c.type === "process" && c.tagId === tagId) {
          const updated = { ...c, toSave: true };
          pendingChangesRef.current.set(c.id, updated);
          return updated;
        }

        return c;
      });
      return { configs: newConfigs };
    });
  }, []);

  /**
   * Update process's max days
   */
  const updateProcessMaxDays = useCallback(
    (tagId: string, processId: string, maxDays: number) => {
      setState((prev) => {
        const newConfigs = prev.configs.map((c) => {
          if (c.type === "process" && c.id === processId && c.tagId === tagId) {
            const updated = { ...c, maxDays, toSave: true };
            pendingChangesRef.current.set(processId, updated);
            return updated;
          }
          return c;
        });
        return { configs: newConfigs };
      });
    },
    [],
  );

  /**
   * Reset all changes and clear pending changes
   */
  const resetAll = useCallback(() => {
    setState({ configs: [...initialStateRef.current.configs] });
    pendingChangesRef.current.clear();
  }, []);

  /**
   * Get tag by id (stable reference using ref)
   */
  const getTag = useCallback((tagId: string): TagConfig | undefined => {
    return stateRef.current.configs.find(
      (c): c is TagConfig => c.type === "tag" && c.id === tagId,
    );
  }, []);

  /**
   * Get all processes for a tag (stable reference using ref)
   */
  const getProcessesForTag = useCallback((tagId: string): ProcessConfig[] => {
    return stateRef.current.configs.filter(
      (c): c is ProcessConfig => c.type === "process" && c.tagId === tagId,
    );
  }, []);

  /**
   * Get changes summary for API
   */
  const getChangesSummary =
    useCallback((): UpdateDeadlineSuspensionConfigType => {
      if (!hasAnyChanges(stateRef.current, pendingChangesRef.current)) {
        return { configs: [] };
      }
      return getChangesToSave(stateRef.current, pendingChangesRef.current);
    }, []);

  /**
   * Check if there are any changes (current page or pending from other pages)
   */
  const hasChanges = useMemo(
    () => hasAnyChanges(state, pendingChangesRef.current),
    [state],
  );

  return {
    state,
    initialize,
    updateTagMaxDays,
    toggleGeneralConfig,
    updateProcessMaxDays,
    resetAll,
    hasChanges,
    getChangesToSave,
    getTag,
    getProcessesForTag,
    getChangesSummary,
  };
}
